/**
 * Die Brotkrume der Kopfleiste.
 *
 * Der Entwurf setzt links oben `CUB / Heute`, `CUB / Aufgaben / Board`,
 * `CUB / Auto Q&A / Lauf 1284`. Das Kürzel kommt aus dem Kontext, die Kette
 * dahinter aus der Adresse — und weil das eine reine Abbildung von Adresse auf
 * Beschriftung ist, steht sie hier und nicht in der Leiste.
 *
 * Das letzte Glied einer Detailseite (`Lauf 1284`, `CUB-142`) steht **nicht**
 * hier: Es ist keine Eigenschaft der Adresse, sondern der geladenen Daten. Die
 * Seiten geben es über `zusatz` dazu; diese Funktion liefert den Bereich.
 */

/** Reiter des Projektdetails — dieselben Werte wie `REITER` dort. */
const REITER_LABEL: Readonly<Record<string, string>> = {
  uebersicht: "Übersicht",
  puls: "Puls",
  aufgaben: "Aufgaben",
  meilensteine: "Meilensteine",
  lieferung: "Lieferung",
  durchsicht: "Durchsicht",
  regeln: "Regeln",
  klaerungen: "Klärungen",
  seiten: "Seiten",
  personen: "Personen",
  einstellungen: "Einstellungen",
};

const EINSTELLUNGEN_LABEL: Readonly<Record<string, string>> = {
  persoenlich: "Persönlich",
  zeitplanung: "Zeitplanung",
  sichten: "Sichten",
  team: "Team",
  instanz: "Instanz",
  metarr: "Metarr",
  gitlab: "GitLab",
  // Alte Pfade (Weiterleitungen) behalten lesbare Brotkrumen.
  allgemein: "Persönlich",
  personen: "Team",
  kalender: "Zeitplanung",
  kapazitaet: "Zeitplanung",
};

export function brotkrume(
  pfad: string,
  reiter?: unknown,
): readonly string[] {
  if (pfad === "/") return ["Heute"];

  if (pfad === "/aufgaben" || pfad.startsWith("/aufgaben/")) {
    return ["Aufgaben"];
  }
  if (pfad === "/klaerungen") return ["Klärungen"];
  if (pfad === "/benachrichtigungen") return ["Benachrichtigungen"];
  if (pfad === "/seiten") return ["Seiten"];
  /*
   * Auto Q&A und Metarr standen in der Schiene, aber nicht hier — die
   * Kopfleiste blieb auf beiden Seiten leer, obwohl der Kommentar oben
   * ausgerechnet `CUB / Auto Q&A / Lauf 1284` als Beispiel führt. Beide
   * tragen Unterseiten, deshalb `startsWith`.
   */
  if (pfad === "/auto-qa" || pfad.startsWith("/auto-qa/")) {
    return ["Auto Q&A"];
  }
  if (pfad === "/metarr" || pfad.startsWith("/metarr/")) return ["Metarr"];

  if (pfad.startsWith("/einstellungen")) {
    const teil = pfad.split("/")[2];
    const label = teil ? EINSTELLUNGEN_LABEL[teil] : undefined;
    return label ? ["Einstellungen", label] : ["Einstellungen"];
  }

  if (pfad === "/projekte") return ["Projekte"];

  if (pfad.startsWith("/projekte/")) {
    // `/projekte/<id>/<bereich>/…` — der Bereich steht an vierter Stelle.
    const bereich = pfad.split("/")[3];
    if (bereich === "seiten") return ["Seiten"];
    if (bereich === "aufgaben") return ["Aufgaben"];
    if (bereich === "regeln") return ["Regeln"];
    if (bereich === "meilensteine") return ["Meilensteine"];

    // `/projekte/<id>` — der Bereich steht im Reiter. Ohne Reiter ist es Puls,
    // wie `kanonisiereProjektReiter` es auch auflöst.
    if (!bereich) {
      const name = typeof reiter === "string" ? REITER_LABEL[reiter] : undefined;
      return [name ?? "Puls"];
    }
  }

  return [];
}
